import React, { useState, useEffect } from 'react';
import { Pie } from 'react-chartjs-2';
import PercentIcon from '@mui/icons-material/Percent';
import Slider from '@mui/material/Slider';
import { Helmet } from 'react-helmet';
import { useSelector } from 'react-redux';
import { RootState } from '../Redux/store';
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import FavoriteIcon from "@mui/icons-material/Favorite";
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import PDFGenerator from '../pdf/pdf-goalsip';
import ReactGA from 'react-ga4'
import { useDispatch } from "react-redux";
import { GoalSIPData } from '../models/goal-sip';
import { Typography, IconButton } from "@mui/material";
import SaveTitleDialog from '../dialog-box/SaveTitleDialog';
import { useLocation } from "react-router-dom";
import { setCurrencySymbol } from '../Redux/currencySlice';
import { savegoalSIP, updategoalSIP } from '../Database/goal-sipDB';
import SavedSnackbar from '../utils/saved-snackbar';

const GoalSIPCalculator: React.FC<{ goalSip?: GoalSIPData }> = ({ }) => {
  const currencySymbol = useSelector((state: RootState) => state.country.currencySymbol);
  const [goalAmount, setGoalAmount] = useState('10,00,000');
  const [duration, setDuration] = useState('10');
  const [rateOfReturn, setRateOfReturn] = useState('12');
  const [totalInvestment, setTotalInvestment] = useState<number | null>(null);
  const [monthlySIP, setMonthlySIP] = useState<number | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [yearlyData, setYearlyData] = useState<{ year: number; totalInvestment: number; interestGoal: number }[]>([]);
  const [calculatedTotalInvestment, setCalculatedTotalInvestment] = useState<number | null>(null);
  const [calculatedGoalAmount, setCalculatedGoalAmount] = useState<number | null>(null);
  const [icon, setIcon] = useState<React.ReactNode>(<FavoriteBorderIcon />);
  const [title, setTitle] = useState("");
  const location = useLocation();
  const [goalSipData, setgoalSIPData] = useState<GoalSIPData | null>(null);
  const dispatch = useDispatch();
  const [, setSymbol] = useState(currencySymbol);
  const [shouldCalculate, setShouldCalculate] = useState(false);
  const [savedTitle, setSavedTitle] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [isCalculated, setIsCalculated] = useState(false);
  const MAX_VALUE = 1000000000;
  const handleSubmit = async () => {
    let hasError = false;

    if (!title) {
      setError((prevError) => ({
        ...prevError,
        title: "Title is required.",
      }));
      hasError = true;
    } else {
      setError((prevError) => ({ ...prevError, title: "" }));
    }

    if (goalAmount === '') {
      setError((prevError) => ({
        ...prevError,
        goalAmount: 'Goal Amount is required.',
      }));
      hasError = true;
    } else {
      setError((prevError) => ({ ...prevError, goalAmount: '' }));
    }

    if (duration === '') {
      setError((prevError) => ({
        ...prevError,
        duration: 'Investment Duration is required.',
      }));
      hasError = true;
    } else {
      setError((prevError) => ({ ...prevError, duration: '' }));
    }

    if (rateOfReturn === '') {
      setError((prevError) => ({
        ...prevError,
        rateOfReturn: 'Expected Rate of Return is required.',
      }));
      hasError = true;
    } else {
      setError((prevError) => ({ ...prevError, rateOfReturn: '' }));
    }

    if (hasError) return;
    const updatedData: GoalSIPData = {
      title: title,
      goalAmount: parseFloat(goalAmount.replace(/,/g, "")),
      duration: parseFloat(duration),
      rateOfReturn: parseInt(rateOfReturn),
      currencySymbol: currencySymbol,
    };

    if (goalSipData?.id) {
      await updategoalSIP(goalSipData.id, updatedData);
      console.log(`Goal SIP with ID ${goalSipData.id} updated successfully.`);
    } else {
      await savegoalSIP(updatedData);
      console.log("New goal SIP created successfully.");
    }
    setSnackbarMessage("Saved successfully!");
    setSnackbarOpen(true);
    setIcon(<FavoriteIcon style={{ color: "red" }} />);
    handleDialogClose();
    setTitle("");
  };

  const calculateYearlyData = (goalValue: number, monthlySIP: number, duration: number, rateValue: number) => {
    const yearlyData = [];
    const monthlyRate = rateValue / 100 / 12;

    for (let year = 1; year <= duration; year++) {
      const currentTotal = monthlySIP * 12 * year;
      let expectedValue = 0;

      for (let month = 0; month < year * 12; month++) {
        expectedValue += monthlySIP * Math.pow(1 + monthlyRate, (year * 12 - month));
      }

      const interestGoal = expectedValue - currentTotal;

      yearlyData.push({
        year,
        totalInvestment: currentTotal,
        interestGoal,
      });
    }

    return yearlyData;
  };


  const [error, setError] = useState({
    goalAmount: '',
    duration: '',
    rateOfReturn: ''
  });

  const formatNumberWithCommas = (value: number) => {
    return new Intl.NumberFormat('en-IN').format(value);
  };

  const handleGoalAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value.replace(/,/g, '').replace(/\D/g, '');
    const numericValue = parseFloat(inputValue);

    if (inputValue === "0") {
      setError((prevError) => ({
        ...prevError,
        goalAmount: "Goal Amount can't start with 0.",
      }));
      setGoalAmount("");
    } else if (numericValue < 5000 && inputValue !== "") {
      setError((prevError) => ({
        ...prevError,
        goalAmount: "Goal Amount must be at least " + currencySymbol + "5000.",
      }));
      setGoalAmount(inputValue);
    } else if (numericValue > 10000000) {
      setError((prevError) => ({
        ...prevError,
        goalAmount: "Goal Amount can't exceed " + currencySymbol + "1,00,00,000.",
      }));
      setGoalAmount("1,00,00,000");
    } else {
      setError((prevError) => ({
        ...prevError,
        goalAmount: "",
      }));
      setGoalAmount(inputValue === "" ? "" : formatNumberWithCommas(numericValue));
    }
  };


  const handleDurationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value.replace(/\D/g, '');
    if (inputValue === '' || (inputValue && Number(inputValue) <= 50 && Number(inputValue) >= 1)) {
      setDuration(inputValue);
      setError((prevError) => ({ ...prevError, duration: '' }));
    }
  };

  const handleRateOfReturnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    const regex = /^\d*\.?\d{0,2}$/;

    if (inputValue === '' || regex.test(inputValue)) {
      let numericValue: number | null = null;

      if (inputValue !== '') {
        numericValue = parseFloat(inputValue);
      }

      if (inputValue === '' || (numericValue !== null && numericValue <= 30 && numericValue >= 1)) {
        setRateOfReturn(inputValue);
        setError((prevError) => ({ ...prevError, rateOfReturn: '' }));
      }
    }
  };

  const handleReset = () => {
    ReactGA.event({ 'category': 'Goal SIP calculator', 'action': 'Goal SIP Reset Button', 'label': 'Goal SIP Reset' })
    setGoalAmount('10,00,000');
    setDuration('10');
    setRateOfReturn('12');
    setTotalInvestment(null);
    setMonthlySIP(null);
    setError({ goalAmount: '', duration: '', rateOfReturn: '' });
  };

  const handleCalculate = () => {

    ReactGA.event({ 'category': 'Goal SIP calculator', 'action': 'Goal SIP Calculate Button', 'label': 'Goal SIP Calculator' })
    let hasError = false;

    if (goalAmount === '') {
      setError((prevError) => ({ ...prevError, goalAmount: 'Please fill in the Goal Amount.' }));
      hasError = true;
    }
    if (duration === '') {
      setError((prevError) => ({ ...prevError, duration: 'Please fill in the Investment Duration.' }));
      hasError = true;
    }
    if (rateOfReturn === '') {
      setError((prevError) => ({ ...prevError, rateOfReturn: 'Please fill in the Expected Rate of Return.' }));
      hasError = true;
    }

    if (hasError) return;

    const goalValue = parseFloat(goalAmount.replace(/,/g, ''));
    const durationValue = parseFloat(duration);
    const rateValue = parseFloat(rateOfReturn);
    const monthlyRate = rateValue / 100 / 12;


    if (isNaN(goalValue) || isNaN(durationValue) || isNaN(rateValue)) {
      alert('Please enter valid numbers for all fields.');
      return;
    }

    const monthlySIPAmount = (goalValue * monthlyRate) / (Math.pow(1 + monthlyRate, durationValue * 12) - 1);
    const totalInvestmentValue = monthlySIPAmount * durationValue * 12;

    setTotalInvestment(totalInvestmentValue);
    setMonthlySIP(monthlySIPAmount);
    setCalculatedTotalInvestment(totalInvestmentValue);
    setCalculatedGoalAmount(goalValue);
    setIsCalculated(true);

    const yearlyData = calculateYearlyData(goalValue, monthlySIPAmount, durationValue, rateValue);
    setYearlyData(yearlyData);
  };


  useEffect(() => {
    const { goalSip: passedGoal, currencySymbol: passedCurrencySymbol } = location.state || {};

    if (passedCurrencySymbol) {
      dispatch(setCurrencySymbol(passedCurrencySymbol));
      setSymbol(passedCurrencySymbol);
    }

    if (passedGoal) {
      setgoalSIPData(passedGoal);
      setIcon(<FavoriteIcon style={{ color: "red" }} />);
      setGoalAmount(formatNumberWithCommas(passedGoal.goalAmount));
      setDuration(passedGoal.duration.toString());
      setRateOfReturn(passedGoal.rateOfReturn.toString());
      setTitle(passedGoal.title);
      setSavedTitle(passedGoal.title || "");

    }
    setShouldCalculate(true);
  }, [location.state, goalSipData]);



  useEffect(() => {
    if (shouldCalculate) {
      handleCalculate();
      setShouldCalculate(false);
    }
  }, [shouldCalculate]);

  const calculateGoalSIP = () => {
    handleCalculate();
  };


  const total = (calculatedTotalInvestment !== null ? calculatedTotalInvestment : 0) +
    (calculatedGoalAmount !== null ? calculatedGoalAmount : 0);

  const chartData = {
    labels: [
      `Total Investment: ${((calculatedTotalInvestment !== null ? calculatedTotalInvestment : 0) / total * 100).toFixed(2)}%`,
      `Goal Amount: ${((calculatedGoalAmount !== null ? calculatedGoalAmount : 0) / total * 100).toFixed(2)}%`
    ],
    datasets: [
      {
        data: [
          calculatedTotalInvestment !== null ? calculatedTotalInvestment : 0,
          calculatedGoalAmount !== null ? calculatedGoalAmount : 0,
        ],
        backgroundColor: ['#36A2EB', '#FF6384'],
        hoverBackgroundColor: ['#36A2EB', '#FF6384'],
      },
    ],
  };

  const handleDialogOpen = () => {
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setTitle("");
  };

  const handleCancel = () => {
    handleDialogClose();
  };
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      calculateGoalSIP();
    }
  };


  const handleSnackbarClose = (
    event: React.SyntheticEvent | Event,
    reason: "timeout" | "clickaway" | "escapeKeyDown"
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  function formatNumberInIndianStyle(num: number): string {
    return num.toLocaleString('en-IN'); // Formats number in Indian numbering system
  }
  const MAX_GOAL_AMOUNT = 10000000; // Set maximum goal amount limit


  return (
    <>
      <Helmet>
        <title>Goal SIP Calculator</title>
        <meta
          name="description"
          content="Plan your financial goals with our Goal SIP Calculator. Calculate the monthly investment needed to achieve your target amount and stay on track with your savings plan."
        />
        <link rel="canonical" href="/goal-sip-calculator.html" />
      </Helmet>
      <SavedSnackbar
        open={snackbarOpen}
        message={snackbarMessage}
        onClose={handleSnackbarClose}
      />
      <div className="container mt-3" onKeyPress={handleKeyPress}>
        <div className="row justify-content-center">
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-2" style={{ fontFamily: "serif", fontSize: "30px", fontWeight: "bold" }}>
              Goal SIP Calculator            </h5>
          </div>
          <p>A Goal SIP Calculator helps plan monthly investments to achieve specific financial goals over time.</p>
          {savedTitle && (
            <Typography variant="h6" style={{ marginLeft: '20px' }}>
              {savedTitle}
            </Typography>
          )}
          <div className="col-12 col-md-6 mb-4 ">

            <div className="card shadow-sm" style={{ width: '100%', height: '550px', margin: '0 auto', paddingTop: '20px', overflow: 'auto' }}>

              <div className="card-body">
                <div className="mb-3">
                  <label htmlFor="goalAmount" className="form-label">
                    Goal Amount ({currencySymbol})
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="goalAmount"
                    value={goalAmount}
                    onChange={handleGoalAmountChange} />
                  {error.goalAmount && <p className="text-danger">{error.goalAmount}</p>}
                </div>
                <div className="mb-3 d-flex justify-content-center">
                  <Slider
                    min={5000}
                    max={10000000}
                    step={1000}
                    value={parseFloat(goalAmount.replace(/,/g, ''))}
                    onChange={(e, newValue) => setGoalAmount(newValue.toString())}
                    valueLabelDisplay="auto"
                    shiftStep={30}
                    sx={{ width: '95%' }}
                  />
                </div>

                <div className="mb-3">
                  <label htmlFor="duration" className="form-label">
                    Investment Duration (Years)
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="duration"
                    value={duration}
                    onChange={handleDurationChange}
                  />
                  {error.duration && <p className="text-danger">{error.duration}</p>}
                </div>

                <div className="mb-3">
                  <label htmlFor="rateOfReturn" className="form-label">
                    Expected Rate of Return (%)
                  </label>
                  <div className="input-group">
                    <input
                      type="text"
                      className="form-control"
                      id="rateOfReturn"
                      value={rateOfReturn}
                      onChange={handleRateOfReturnChange}
                    />
                    <span className="input-group-text">
                      <PercentIcon />
                    </span>
                  </div>
                  {error.rateOfReturn && <p className="text-danger">{error.rateOfReturn}</p>}

                </div>

                <div className="d-flex justify-content-center " style={{ marginTop: '60px' }}>
                  <button
                    className="btn btn-primary btn-lg "
                    onClick={calculateGoalSIP}

                  >
                    Calculate
                  </button>
                </div>
                <div className='d-flex justify-content-center'>
                  <a
                    onClick={handleReset}
                    style={{ cursor: "pointer", textDecoration: "none" }}
                    className=" pt-2"
                  >
                    Reset
                  </a>
                </div>
              </div>
            </div>
          </div>
          {totalInvestment !== null && monthlySIP !== null && (
            <div className="col-12 col-md-6 mb-4">
              <div className="card shadow-sm">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center">
                    <h5 className="card-title">Result</h5>
                    <IconButton color="secondary" onClick={handleDialogOpen}>
                      {icon}
                    </IconButton>
                  </div>
                  <p className="card-text d-flex justify-content-between">
                    <strong>Goal Amount:</strong>
                    <span className="text-end">
                      {currencySymbol}{" "}
                      {calculatedGoalAmount !== null ? formatNumberInIndianStyle(calculatedGoalAmount) : ""}
                    </span>
                  </p>

                  <p className="card-text">
                    <strong>Total Investment:</strong>
                    <span className="float-end">
                      {currencySymbol}  {totalInvestment !== null ? `${formatNumberWithCommas(Number(Math.ceil(totalInvestment).toFixed(2)))}` : ''}
                    </span>
                  </p>
                  <p className="card-text">
                    <strong>Monthly SIP Amount:</strong>
                    <span className="float-end">
                      {currencySymbol}  {monthlySIP !== null ? `${formatNumberWithCommas(Number(Math.ceil(monthlySIP).toFixed(2)))}` : ''}
                    </span>
                  </p>
                </div>
              </div>

              {calculatedTotalInvestment !== null && calculatedGoalAmount !== null && (
                <div className="card mt-3 shadow-sm" style={{ width: '100%', margin: '0 auto' }}>
                  <div className="card-body">
                    <h5 className="card-title mb-4">Investment Breakdown</h5>
                    <div className="d-flex justify-content-center align-items-center" style={{ width: '100%', height: '270px', margin: '0 auto' }}>
                      <Pie data={chartData} />
                    </div>
                  </div>
                </div>
              )}

            </div>
          )}
          <div className="d-flex justify-content-end" style={{ marginTop: "45px", paddingLeft: "90px" }}>
            {totalInvestment !== null && monthlySIP !== null && (
              <PDFGenerator
                yearlyData={yearlyData}
                goalAmount={goalAmount}
                duration={duration}
                rateOfReturn={rateOfReturn}
                totalInvestment={totalInvestment}
                monthlySIP={monthlySIP}
                isCalculated={isCalculated}
              />
            )}
          </div>
        </div>
        <SaveTitleDialog
          open={dialogOpen}
          onClose={handleDialogClose}
          title={title}
          setTitle={setTitle}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
        />
      </div>
    </>
  );
};

export default GoalSIPCalculator;